import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import ZoneLoadingScreen from './ZoneLoadingScreen';
import ServerTimeService from '../services/serverTimeService';
import { setUser } from '../../core/store/slices/userSlice';
import { getApiBaseUrl } from '../../core/config/api';

interface PostLoginLoaderProps {
  userId: number;
  onComplete: () => void;
}

const PostLoginLoader: React.FC<PostLoginLoaderProps> = ({ userId, onComplete }) => {
  const dispatch = useDispatch();
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState('Connexion au serveur...');
  const [error, setError] = useState<string | null>(null);
  const onCompleteRef = useRef(onComplete);
  const userIdRef = useRef(userId);

  // Mettre à jour les refs à chaque changement
  useEffect(() => {
    onCompleteRef.current = onComplete;
    userIdRef.current = userId;
  }, [onComplete, userId]);

  useEffect(() => {
    let isMounted = true;
    let progressInterval: NodeJS.Timeout | null = null;
    let timer: NodeJS.Timeout | null = null;

    // Démarrer la progression animée fluide (exactement comme NavigationLoader)
    let currentProgress = 0;
    setProgress(0);
    setCurrentMessage('Connexion au serveur...');

    // Animation de progression fluide (exactement comme NavigationLoader)
    progressInterval = setInterval(() => {
      if (!isMounted) return;
      
      // Progression accélérée au début, ralentie vers la fin
      let increment;
      if (currentProgress < 30) {
        increment = Math.random() * 10 + 5; // 5-15% au début
      } else if (currentProgress < 70) {
        increment = Math.random() * 6 + 3; // 3-9% au milieu
      } else {
        increment = Math.random() * 3 + 1; // 1-4% vers la fin
      }
      
      currentProgress += increment;
      
      if (currentProgress >= 95) {
        currentProgress = 95; // S'arrêter à 95% en attendant le timeout
        if (progressInterval) clearInterval(progressInterval);
        progressInterval = null;
      }
      
      if (isMounted) {
        setProgress(Math.min(currentProgress, 95));
      }
    }, 100);

    // Lancer toutes les opérations en parallèle avec le chargement
    const operationsPromise = (async () => {
      try {
        // Étape 1: Charger le profil complet de l'utilisateur avec le rôle
        setCurrentMessage('Chargement du profil...');
        // Pas de délai artificiel, on lance directement la requête
        
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const response = await fetch(`${getApiBaseUrl(true)}/auth/profile`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.user) {
                // Mettre à jour l'utilisateur dans Redux avec toutes les données incluant roles_id
                const userForRedux = {
                  ...data.user,
                  activated: data.user.activated !== undefined ? data.user.activated : true,
                  bann: 0,
                  premium: false,
                };
                dispatch(setUser(userForRedux as any));
                console.log('✅ Profil utilisateur chargé:', userForRedux);
                console.log('✅ roles_id:', userForRedux.roles_id);
                console.log('✅ role_name:', userForRedux.role_name);
              } else {
                console.error('❌ Erreur lors du chargement du profil:', data);
              }
            } else {
              const errorData = await response.json().catch(() => ({}));
              console.error('❌ Erreur HTTP lors du chargement du profil:', response.status, errorData);
            }
          } catch (err) {
            console.warn('Impossible de charger le profil complet, utilisation des données de connexion', err);
          }
        }

        // Étape 2: Synchronisation de l'heure serveur
        setCurrentMessage('Synchronisation de l\'heure serveur FR...');
        const serverTimeService = ServerTimeService.getInstance();
        const syncSuccess = await serverTimeService.syncServerTime();
        
        if (!syncSuccess) {
          console.warn('Impossible de synchroniser l\'heure serveur, utilisation de l\'heure locale');
        } else {
          // Démarrer la synchronisation automatique
          serverTimeService.startAutoSync(60000); // Sync toutes les minutes
        }

        // Étape 3: Vérification des fichiers JSON de sauvegarde
        setCurrentMessage('Vérification des sauvegardes...');
        if (token) {
          try {
            const filesResponse = await fetch(`${getApiBaseUrl(true)}/buildings/files`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (filesResponse.ok) {
              const filesData = await filesResponse.json();
              if (filesData.success && filesData.files) {
                const existingFiles = filesData.files.filter((f: any) => f.exists);
                console.log(`✅ Fichiers de sauvegarde trouvés: ${existingFiles.length}/${filesData.files.length}`);
              }
            }
          } catch (err) {
            console.debug('Vérification des fichiers de sauvegarde ignorée:', err);
          }
        }

        // Étape 4: Initialisation du jeu
        setCurrentMessage('Initialisation du jeu...');
      } catch (err) {
        console.error('Erreur lors des opérations de chargement:', err);
        throw err;
      }
    })();

    // Temps minimum de chargement pour l'effet visuel (comme NavigationLoader)
    const minLoadingTime = 500; // 500ms minimum (réduit pour un chargement plus rapide)
    timer = setTimeout(async () => {
      try {
        // Attendre que les opérations soient terminées
        await operationsPromise;
        
        if (!isMounted) return;
        
        // Finir la progression jusqu'à 100%
        setProgress(100);
        setCurrentMessage('Finalisation...');
        
        setTimeout(() => {
          if (isMounted) {
            onCompleteRef.current();
          }
        }, 200); // Réduit de 400ms à 200ms pour un chargement plus rapide
      } catch (err: any) {
        if (!isMounted) return;
        
        console.error('Erreur lors du chargement:', err);
        setError(err.message || 'Erreur lors du chargement');
        // Continuer quand même vers le dashboard après un délai
        setTimeout(() => {
          if (isMounted) {
            onCompleteRef.current();
          }
        }, 2000);
      }
    }, minLoadingTime);

    // Cleanup
    return () => {
      isMounted = false;
      if (progressInterval) clearInterval(progressInterval);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Exécuter une seule fois au montage

  if (error) {
    return (
      <ZoneLoadingScreen 
        loadingText={`Erreur: ${error}. Redirection en cours...`}
        progress={100}
      />
    );
  }

  return (
    <ZoneLoadingScreen 
      loadingText={currentMessage}
      progress={progress}
    />
  );
};

export default PostLoginLoader;

